// The integration point for the existing LiveSwitch web app.
//
// The host app already creates a downstream per remote upstream and already has
// remote audio arriving. It hands us the RemoteMedia it built; we take a
// reference to the audio track and nothing else. No new connections, no change
// to the channel, no change to anyone's call experience (spec s4, s12).
//
// In the host app:
//
//   // where the downstream opens
//   window.virtualInspection.registerRemoteMedia(clientId, remoteMedia);
//   // where it closes
//   window.virtualInspection.unregisterRemoteMedia(clientId);
//
// Standalone (this repo running on its own), nothing calls these and the call
// view offers the development sources instead.

import { useSyncExternalStore } from 'react';
import { createLiveSwitchFeed } from './liveswitchFeed';
import type { ChannelLike, RemoteMediaLike } from './types';

const feed = createLiveSwitchFeed();

/** Reserved id for our own microphone in the shared audio feed. */
const LOCAL_AUDIO_ID = 'local-microphone';

const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

feed.onAdded(notify);
feed.onRemoved(notify);

export const liveSwitchBridge = {
  feed,
  registerRemoteMedia(clientId: string, remoteMedia: RemoteMediaLike): void {
    feed.register(clientId, remoteMedia);
  },
  unregisterRemoteMedia(clientId: string): void {
    feed.unregister(clientId);
  },
  /** Our own microphone, so the inspector's half of the call is transcribed. */
  registerLocalAudio(track: MediaStreamTrack): void {
    feed.registerTrack(LOCAL_AUDIO_ID, track);
  },
  unregisterLocalAudio(): void {
    feed.unregister(LOCAL_AUDIO_ID);
  },
  attachChannel(channel: ChannelLike): () => void {
    return feed.attachChannel(channel);
  },
  trackCount(): number {
    return feed.current().length;
  },
};

declare global {
  interface Window {
    virtualInspection?: typeof liveSwitchBridge;
  }
}

if (typeof window !== 'undefined') window.virtualInspection = liveSwitchBridge;

/** Re-renders the call view as remote participants come and go. */
export function useLiveSwitchTrackCount(): number {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => feed.current().length,
    () => 0,
  );
}
